import { useCallback, useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { PrimeIcons } from 'primereact/api';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { classNames } from 'primereact/utils';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { z } from 'zod';

import { useGetAdUsersQuery } from '@/app/services/authentication';
import {
  useCreateIssueManagerMutation,
  useDeleteIssueManagerMutation,
} from '@/app/services/issues';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { RequestIssueManagerFragment, RequestIssueType } from '@/rtk-query.gql';

import { CreateIssueManagerInput, TYPE_OPTIONS } from './constants';

import './ManagerModal.css';

const schema = z.object({
  adUserId: z.string(),
  issueType: z.nativeEnum(RequestIssueType),
});

type Values = z.infer<typeof schema>;

type ManagerModalProps = {
  input: RequestIssueManagerFragment | CreateIssueManagerInput | undefined;
  close: () => void;
  show: (
    input: RequestIssueManagerFragment | CreateIssueManagerInput | undefined,
  ) => void;
};

export const ManagerModal = ({ close, input }: ManagerModalProps) => {
  const dispatch = useDispatch();

  const [createManager] = useCreateIssueManagerMutation();
  const [deleteManager] = useDeleteIssueManagerMutation();

  const { data: adUsers } = useGetAdUsersQuery({});

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    reset(input as Values);
  }, [input, reset]);

  const hide = useCallback(() => {
    close();
    reset();
  }, [close, reset]);

  const onSubmit = useCallback(
    async (values: Values) => {
      if (!input || 'id' in input) {
        return;
      }

      try {
        await createManager({
          adUserId: values.adUserId,
          issueType: values.issueType,
        }).unwrap();

        dispatch(
          addToast({
            severity: 'success',
            summary: 'Success',
            detail: 'Added the user.',
            life: ToastLife.DEFAULT,
          }),
        );
        hide();
      } catch (error: unknown) {
        dispatch(
          addToast({
            severity: 'error',
            summary: 'Error',
            detail:
              (error as { message: string }).message === 'DUPLICATE_ERROR'
                ? 'A record for this user and type already exists.'
                : 'Encountered an error while creating the user.',
            life: ToastLife.DEFAULT,
          }),
        );
      }
    },
    [createManager, dispatch, hide, input],
  );

  const handleDelete = useCallback(() => {
    if (!input || !('id' in input)) return;

    confirmDialog({
      header: 'Confirm',
      message: 'Are you sure you want to remove this user?',
      icon: PrimeIcons.EXCLAMATION_TRIANGLE,
      accept: async () => {
        try {
          await deleteManager({ id: input.id }).unwrap();
          dispatch(
            addToast({
              severity: 'success',
              summary: 'Success',
              detail: 'Removed the user.',
              life: ToastLife.DEFAULT,
            }),
          );
          hide();
        } catch (error) {
          dispatch(
            addToast({
              severity: 'error',
              summary: 'Error',
              detail: 'Encountered an error while removing the user.' + error,
              life: ToastLife.DEFAULT,
            }),
          );
        }
      },
    });
  }, [deleteManager, dispatch, hide, input]);

  return (
    <>
      <ConfirmDialog />
      <Dialog
        visible={!!input}
        onHide={hide}
        header={input && 'id' in input ? 'Edit User' : 'Add User'}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="manager-modal-form">
          <div className="manager-modal-form__fields">
            <div className="label-input-group">
              <label htmlFor="issueType">Type</label>
              <Controller
                name="issueType"
                control={control}
                render={({ field, fieldState }) => (
                  <Dropdown
                    id={field.name}
                    {...field}
                    options={TYPE_OPTIONS}
                    className={classNames({ 'p-invalid': fieldState.error })}
                    disabled={isSubmitting}
                  />
                )}
              />
            </div>

            <div className="label-input-group">
              <label htmlFor="adUserId">User</label>
              <Controller
                name="adUserId"
                control={control}
                render={({ field, fieldState }) => (
                  <Dropdown
                    id={field.name}
                    {...field}
                    options={adUsers}
                    className={classNames({ 'p-invalid': fieldState.error })}
                    optionLabel="name"
                    optionValue="id"
                    filter
                    filterBy="name"
                    disabled={isSubmitting}
                  />
                )}
              />
            </div>
          </div>

          <div className="manager-modal-form__buttons">
            {input && 'id' in input && typeof input.id === 'number' && (
              <Button
                type="button"
                label="Delete"
                icon={PrimeIcons.TRASH}
                outlined
                disabled={isSubmitting}
                onClick={handleDelete}
              />
            )}

            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              onClick={hide}
              disabled={isSubmitting}
            />
            <Button
              type="submit"
              label="Save"
              disabled={isSubmitting || (input && 'id' in input)}
            />
          </div>
        </form>
      </Dialog>
    </>
  );
};
